import axios from 'axios'
import { Configuration } from '../constants'
import { apiErrorParser } from './api-error-parser'
import { progressIndicator } from './progress-indicator'

export default class Api {
  constructor () {
    const options = {
      baseURL: Configuration.API_HOST,
      timeout: Configuration.API_TIMEOUT,
      withCredentials: true,
    }
    if (Configuration.BASIC_AUTH_ON_API === '1') {
      if (Configuration.BASIC_AUTH_USER_NAME && Configuration.BASIC_AUTH_PASSWORD) {
        options.auth = {
          username: Configuration.BASIC_AUTH_USER_NAME,
          password: Configuration.BASIC_AUTH_PASSWORD,
        }
      }
    }
    this.req = axios.create(options)
  }

  get (...args) {
    return progressIndicator.wrap(this.req.get)(...args)
  }

  post (...args) {
    return progressIndicator.wrap(this.req.post)(...args)
  }

  put (...args) {
    return progressIndicator.wrap(this.req.put)(...args)
  }

  delete (...args) {
    return progressIndicator.wrap(this.req.delete)(...args)
  }

  download (url, params) {
    return this.req.post(url, params)
      .then((res) => {
        if (res.status === 200) {
          const disposition = res.request.getResponseHeader('content-disposition')
          const matches = /"([^"]*)"/.exec(disposition)
          const filename = (matches != null && matches[1] ? matches[1] : 'DownloadJobs.csv')

          const blob = new Blob([res.data])
          if (window.navigator && window.navigator.msSaveBlob) {
            window.navigator.msSaveBlob(blob, filename)
          } else if (window.URL && window.URL.createObjectURL) {
            const link = document.createElement('a')
            link.href = window.URL.createObjectURL(blob)
            link.download = filename
            document.body.appendChild(link)
            link.click()
            setTimeout(() => {
              document.body.removeChild(link)
              window.URL.revokeObjectURL(link.href)
            }, 0)
          }
        }
      })
  }

  async fetchMaster () {
    return this.get('/masters')
      .then(res =>
        Promise.resolve(res.data.results),
      )
      .catch(error =>
        Promise.reject(apiErrorParser.parse(error)),
      )
  }

  async signIn ({ email, password, sessionKeeping }) {
    return this.post('/client_users/sessions', { email, password, session_keeping: sessionKeeping })
      .then(res =>
        Promise.resolve(res.data),
      )
      .catch(error =>
        Promise.reject(apiErrorParser.parse(error)),
      )
  }

  async signOut () {
    return this.delete('/client_users/sessions', {})
      .catch(error =>
        Promise.reject(apiErrorParser.parse(error)),
      )
  }

  async fetchCompanies (query) {
    return this.get('/companies', { params: query })
      .then((res) => {
          const { meta, results: index } = res.data
          return Promise.resolve({ meta, index })
        },
      )
      .catch(error =>
        Promise.reject(apiErrorParser.parse(error)),
      )
  }

  async fetchCompany (id) {
    return this.req.get(`/companies/${id}`)
      .then(res =>
        Promise.resolve(res.data.results),
      )
      .catch(error =>
        Promise.reject(apiErrorParser.parse(error)),
      )
  }

  async fetchCorporations (query) {
    return this.get('/corporations', { params: query })
      .then((res) => {
          const { meta, results: index } = res.data
          return Promise.resolve({ meta, index })
        },
      )
      .catch(error =>
        Promise.reject(apiErrorParser.parse(error)),
      )
  }

  async fetchCorporation (id) {
    return this.req.get(`/corporations/${id}`)
      .then(res =>
        Promise.resolve(res.data.results),
      )
      .catch(error =>
        Promise.reject(apiErrorParser.parse(error)),
      )
  }

  async fetchCompanyList ({ perPage, page }) {
    return this.get('/job_marks', { params: { per_page: perPage, page } })
      .then((res) => {
          const { meta, results: index } = res.data
          return Promise.resolve({ meta, index })
        },
      )
      .catch(error =>
        Promise.reject(apiErrorParser.parse(error)),
      )
  }

  async fetchUser () {
    return this.get('/client_users/current_user')
      .then(res =>
        Promise.resolve(res.data.results[0]),
      )
      .catch(error =>
        Promise.reject(apiErrorParser.parse(error)),
      )
  }

  async fetchJobs (query) {
    return this.get('/jobs', { params: query })
      .then((res) => {
          const { meta, results: index } = res.data
          return Promise.resolve({ meta, index })
        },
      )
      .catch(error =>
        Promise.reject(apiErrorParser.parse(error)),
      )
  }

  async fetchJob (id) {
    return this.req.get(`/jobs/${id}`)
      .then(res =>
        Promise.resolve(res.data.results),
      )
      .catch(error =>
        Promise.reject(apiErrorParser.parse(error)),
      )
  }

  async fetchGroupingJobs (query) {
    return this.get('/grouping_jobs', { params: query })
      .then((res) => {
          const { meta, results: index } = res.data
          return Promise.resolve({ meta, index })
        },
      )
      .catch(error =>
        Promise.reject(apiErrorParser.parse(error)),
      )
  }

  async fetchGroupingJob (id) {
    return this.req.get(`/grouping_jobs/${id}`)
      .then(res =>
        Promise.resolve(res.data.results),
      )
      .catch(error =>
        Promise.reject(apiErrorParser.parse(error)),
      )
  }

  async updateCompanyComment ({ companyId, comment }) {
    return this.post('/company_comments', { company_id: companyId, comment })
      .catch(error =>
        Promise.reject(apiErrorParser.parse(error)),
      )
  }

  async updateExclusionCompanyName ({ companyName, enable }) {
    return this.post('/exclusion_company_names', { company_name: companyName, enable })
      .catch(error =>
        Promise.reject(apiErrorParser.parse(error)),
      )
  }

  async addExclusionCompanyName ({ companyName }) {
    return this.post('/exclusion_company_names/add', { company_name: companyName })
      .catch(error =>
        Promise.reject(apiErrorParser.parse(error)),
      )
  }

  async fetchExclusionCompanyName () {
    return this.get('/exclusion_company_names')
      .then(res =>
        Promise.resolve(res.data),
      )
      .catch(error =>
        Promise.reject(apiErrorParser.parse(error)),
      )
  }

  async updateInclusionCompanyName ({ companyName, enable }) {
    return this.post('/inclusion_company_names', { company_name: companyName, enable })
      .catch(error =>
        Promise.reject(apiErrorParser.parse(error)),
      )
  }

  async addInclusionCompanyName ({ companyName }) {
    return this.post('/inclusion_company_names/add', { company_name: companyName })
      .catch(error =>
        Promise.reject(apiErrorParser.parse(error)),
      )
  }

  async fetchInclusionCompanyName () {
    return this.get('/inclusion_company_names')
      .then(res =>
        Promise.resolve(res.data),
      )
      .catch(error =>
        Promise.reject(apiErrorParser.parse(error)),
      )
  }

  async fetchAggregations (query) {
    return this.get('/aggregations', { params: query })
      .then(res =>
        Promise.resolve(res.data),
      )
      .catch(error =>
        Promise.reject(apiErrorParser.parse(error)),
      )
  }

  async applyPasswordReset ({ email }) {
    return this.post('/client_users/forgot_password', { email })
      .catch(error =>
        Promise.reject(apiErrorParser.parse(error)),
      )
  }

  async updatePassword ({ password, resetPasswordToken }) {
    return this.put('/client_users/reset_password', { password, reset_password_token: resetPasswordToken })
      .catch(error =>
        Promise.reject(apiErrorParser.parse(error)),
      )
  }

  async updateUser ({ user }) {
    return this.put('/client_users', { user })
      .catch(error =>
        Promise.reject(apiErrorParser.parse(error)),
      )
  }

  async fetchMySearchConditionIndex () {
    return this.get('/search_conditions')
      .then(res =>
        Promise.resolve(res.data.results),
      )
      .catch(error =>
        Promise.reject(apiErrorParser.parse(error)),
      )
  }

  async createSearchCondition ({ name, condition }) {
    return this.post('/search_conditions', { name, condition })
      .catch(error =>
        Promise.reject(apiErrorParser.parse(error)),
      )
  }

  async updateSearchCondition ({ id, name, condition }) {
    return this.put(`/search_conditions/${id}`, { name, condition })
      .catch(error =>
        Promise.reject(apiErrorParser.parse(error)),
      )
  }

  async destroyMySearchCondition ({ id }) {
    return this.delete(`/search_conditions/${id}`)
      .catch(error =>
        Promise.reject(apiErrorParser.parse(error)),
      )
  }

  async fetchMyAggJobSearchConditionIndex () {
    return this.get('/agg_job_search_conditions')
      .then(res =>
        Promise.resolve(res.data.results),
      )
      .catch(error =>
        Promise.reject(apiErrorParser.parse(error)),
      )
  }

  async createAggJobSearchCondition ({ name, condition }) {
    return this.post('/agg_job_search_conditions', { name, condition })
      .catch(error =>
        Promise.reject(apiErrorParser.parse(error)),
      )
  }

  async updateAggJobSearchCondition ({ id, name, condition }) {
    return this.put(`/agg_job_search_conditions/${id}`, { name, condition })
      .catch(error =>
        Promise.reject(apiErrorParser.parse(error)),
      )
  }

  async destroyAggJobMySearchCondition ({ id }) {
    return this.delete(`/agg_job_search_conditions/${id}`)
      .catch(error =>
        Promise.reject(apiErrorParser.parse(error)),
      )
  }

  async fetchMyJobNotificationSettingIndex () {
    return this.get('/new_job_notification_settings')
      .then(res =>
        Promise.resolve(res.data.results),
      )
      .catch(error =>
        Promise.reject(apiErrorParser.parse(error)),
      )
  }

  async createJobNotificationSetting ({ name, condition, monday, tuesday, wednesday, thursday, friday, saturday, sunday, hour, minutes, lowerLimitCount }) {
    return this.post('/new_job_notification_settings', { name, search_condition: condition, monday, tuesday, wednesday, thursday, friday, saturday, sunday, hour, minutes, lower_limit_count: lowerLimitCount })
      .catch(error =>
        Promise.reject(apiErrorParser.parse(error)),
      )
  }

  async updateJobNotificationSetting ({ id, name, condition, monday, tuesday, wednesday, thursday, friday, saturday, sunday, hour, minutes, lowerLimitCount }) {
    return this.put(`/new_job_notification_settings/${id}`, { name, search_condition: condition, monday, tuesday, wednesday, thursday, friday, saturday, sunday, hour, minutes, lower_limit_count: lowerLimitCount })
      .catch(error =>
        Promise.reject(apiErrorParser.parse(error)),
      )
  }

  async destroyJobNotificationSetting ({ id }) {
    return this.delete(`/new_job_notification_settings/${id}`)
      .catch(error =>
        Promise.reject(apiErrorParser.parse(error)),
      )
  }

  async fetchJobList (query) {
    return this.get('/job_lists', { params: query })
      .then((res) => {
          const { meta, results: index } = res.data
          return Promise.resolve({ meta, index })
        },
      )
      .catch(error =>
        Promise.reject(apiErrorParser.parse(error)),
      )
  }

  async addJobListJobs ({ ids }) {
    return this.post('/job_lists', { job_ids: ids.join(',') })
      .then(res =>
        Promise.resolve(res.data.results),
      )
      .catch(error =>
        Promise.reject(apiErrorParser.parse(error)),
      )
  }

  async removeJobListJobs ({ ids }) {
    return this.delete('/job_lists', { data: { job_ids: ids.join(',') } })
      .then(res =>
        Promise.resolve(res.data.results),
      )
      .catch(error =>
        Promise.reject(apiErrorParser.parse(error)),
      )
  }

  async updateJobList ({ id, salesStatusId, assignedToId, comment }) {
    return this.put(`/job_lists/${id}`, { sales_status_id: salesStatusId, assigned_to_id: assignedToId, comment })
      .catch(error =>
        Promise.reject(apiErrorParser.parse(error)),
      )
  }

  async addJobMarkJobs ({ ids }) {
    return this.post('/job_marks', { company_ids: ids.join(',') })
      .then(res =>
        Promise.resolve(res.data.results),
      )
      .catch(error =>
        Promise.reject(apiErrorParser.parse(error)),
      )
  }

  async removeJobMarkJobs ({ ids }) {
    return this.delete('/job_marks', { data: { company_ids: ids.join(',') } })
      .then(res =>
        Promise.resolve(res.data.results),
      )
      .catch(error =>
        Promise.reject(apiErrorParser.parse(error)),
      )
  }

  async fetchStations ({ stationName }) {
    return this.get('/stations', { params: { station_name: stationName } })
      .then(res =>
        Promise.resolve(res.data.results),
      )
      .catch(error =>
        Promise.reject(apiErrorParser.parse(error)),
      )
  }

  async fetchStation ({ id }) {
    return this.get(`/stations/${id}`)
      .then(res =>
        Promise.resolve(res.data.results),
      )
      .catch(error =>
        Promise.reject(apiErrorParser.parse(error)),
      )
  }

  downloadJobsCsv (jobIds, allColumns) {
    const url = `${Configuration.API_HOST}/jobs/download`
    const params = { job_ids: Array.from(jobIds).join(','), all_columns: allColumns }
    return this.download(url, params)
  }

  downloadGroupingJobsCsv (jobIds, allColumns) {
    const url = `${Configuration.API_HOST}/grouping_jobs/download`
    const params = { job_ids: Array.from(jobIds).join(','), all_columns: allColumns }
    return this.download(url, params)
  }

  downloadCompaniesCsv (companyIds, allColumns) {
    const url = `${Configuration.API_HOST}/companies/download`
    const params = { company_ids: Array.from(companyIds).join(','), all_columns: allColumns }
    return this.download(url, params)
  }

  downloadAggregationJobCsv (query, allColumns) {
    const url = `${Configuration.API_HOST}/aggregation_jobs/download`
    const all = { all_columns: allColumns }
    Object.assign(query, all)
    return this.download(url, query)
  }

  async fetchS3CSVFiles (query) {
    return this.get('/csv_files', { params: query })
      .then((res) => {
          const { meta, results: index } = res.data
          return Promise.resolve({ meta, index })
        },
      )
      .catch(error =>
        Promise.reject(apiErrorParser.parse(error)),
      )
  }

  async fetchClient () {
    return this.get('/client')
      .then(res =>
        Promise.resolve(res.data),
      )
      .catch(error =>
        Promise.reject(apiErrorParser.parse(error)),
      )
  }

  async fetchStartingBusinesses (query) {
    return this.get('/starting_businesses', { params: query })
      .then((res) => {
          const { meta, results: index } = res.data
          return Promise.resolve({ meta, index })
        },
      )
      .catch(error =>
        Promise.reject(apiErrorParser.parse(error)),
      )
  }

  async fetchStartingBusiness (id) {
    return this.req.get(`/starting_businesses/${id}`)
      .then(res =>
        Promise.resolve(res.data.results),
      )
      .catch(error =>
        Promise.reject(apiErrorParser.parse(error)),
      )
  }

  async fetchStartingBusinessList (query) {
    return this.get('/starting_business_lists', { params: query })
      .then((res) => {
          const { meta, results: index } = res.data
          return Promise.resolve({ meta, index })
        },
      )
      .catch(error =>
        Promise.reject(apiErrorParser.parse(error)),
      )
  }

  async addStartingBusinessList ({ ids }) {
    return this.post('/starting_business_lists', { starting_business_ids: ids.join(',') })
      .then(res =>
        Promise.resolve(res.data.results),
      )
      .catch(error =>
        Promise.reject(apiErrorParser.parse(error)),
      )
  }

  async removeStartingBusinessList ({ ids }) {
    return this.delete('/starting_business_lists', { data: { starting_business_ids: ids.join(',') } })
      .then(res =>
        Promise.resolve(res.data.results),
      )
      .catch(error =>
        Promise.reject(apiErrorParser.parse(error)),
      )
  }

  async updateStartingBusinessList ({ id, salesStatusId, assignedToId, comment }) {
    return this.put(`/starting_business_lists/${id}`, { sales_status_id: salesStatusId, assigned_to_id: assignedToId, comment })
      .catch(error =>
        Promise.reject(apiErrorParser.parse(error)),
      )
  }

  async fetchOpportunityGroups () {
    return this.get('/opportunity_groups')
      .then(res =>
        Promise.resolve(res.data),
      )
      .catch(error =>
        Promise.reject(apiErrorParser.parse(error)),
      )
  }

  async fetchSearchTags () {
    return this.get('/search_tags')
      .then(res =>
        Promise.resolve(res.data),
      )
      .catch(error =>
        Promise.reject(apiErrorParser.parse(error)),
      )
  }

  async addAccounts (companyIds, accountParams, opportunityParams) {
    return this.post('/accounts', { company_ids: companyIds.join(','), account_params: accountParams, opportunity_params: opportunityParams })
      .then(res =>
        Promise.resolve(res.data.results),
      )
      .catch(error =>
        Promise.reject(apiErrorParser.parse(error)),
      )
  }

  async fetchAggregationJob (query) {
    return this.get('/aggregation_jobs', { params: query })
      .then(res =>
        Promise.resolve(res.data),
      )
      .catch(error =>
        Promise.reject(apiErrorParser.parse(error)),
      )
  }

  async getJobtypeAi (jobtypeText) {
    return this.get('/jobtype_ai', { params: { jobtype_text: jobtypeText } })
      .then(res =>
        Promise.resolve(res.data),
      )
      .catch(error =>
        Promise.reject(apiErrorParser.parse(error)),
      )
  }
  async getAreaAi (areaText) {
    return this.get('/area_ai', { params: { area_text: areaText } })
      .then(res =>
        Promise.resolve(res.data),
      )
      .catch(error =>
        Promise.reject(apiErrorParser.parse(error)),
      )
  }

  async getAiManuscript (ids) {
    return this.get('/ai_manuscript', { params: { ids } })
      .then(res =>
        Promise.resolve(res.data),
      )
      .catch(error =>
        Promise.reject(apiErrorParser.parse(error)),
      )
  }

  async fetchNews (params = {}) {
    return this.req.get('/news', { params })
      .then(res =>
        Promise.resolve(res.data),
      )
      .catch(error =>
        Promise.reject(apiErrorParser.parse(error)),
      )
  }

  async fetchRecentNews (params = {}) {
    return this.req.get('/news/recent', { params })
      .then(res =>
        Promise.resolve(res.data),
      )
      .catch(error =>
        Promise.reject(apiErrorParser.parse(error)),
      )
  }

  async readNews (id, params = {}) {
    return this.req.post(`/news/${id}/read`, params)
      .then(res =>
        Promise.resolve(res.data),
      )
      .catch(error =>
        Promise.reject(apiErrorParser.parse(error)),
      )
  }

  fetchUploadArchive (params) {
    return this.get('/upload_archives', { params: params || {} })
      .then(res => Promise.resolve(res.data))
      .catch(error => Promise.reject(apiErrorParser.parse(error)))
  }

  fetchUploadArchives (params) {
    return this.get('/upload_archives', { params: params || {} })
      .then(res => Promise.resolve(res.data))
  }

  createUploadArchive (payload) {
    return this.post('/upload_archives', payload)
      .then(res => Promise.resolve(res.data))
      .catch(error => Promise.reject(apiErrorParser.parse(error)))
  }

  createSearchJobUploadArchive (payload) {
    return this.post('/upload_archives/search_job', payload)
      .then(res => Promise.resolve(res.data))
      .catch(error => Promise.reject(apiErrorParser.parse(error)))
  }

  getUploadArchiveDownloadUrl (id, params) {
    return this.get(`/upload_archives/${id}/download`, { params: params || {} })
      .then(res => Promise.resolve(res.data))
  }

  confirmUploadArchiveDownload (id, params) {
    return this.post(`/upload_archives/${id}/confirm_download`, {}, { params: params || {} })
      .then(res => Promise.resolve(res.data))
  }
}

export const api = new Api()



// WEBPACK FOOTER //
// ./src/libs/api.js